import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../context/AuthContext'
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts'
import {
  Bot,
  Phone,
  PhoneCall,
  PhoneForwarded,
  Percent,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Wallet,
  AlertTriangle,
  Server,
  Cpu,
  HardDrive,
  Database,
  Clock,
  UserCheck,
  UserX,
  Inbox,
  Plus,
  Minus,
  Activity,
  AlertCircle,
  XCircle,
  WifiOff,
  ArrowRight,
  RefreshCw,
  BarChart3,
  Crown,
  ChevronDown,
  ExternalLink,
  Mic,
  Tag,
  UserCircle,
  Cloud,
  FolderOpen,
  PhoneOff
} from 'lucide-react'
import {
  monitoringApi,
  clientAdminApi,
  financialsApi,
  backupApi,
  dialerApi,
  avatarApi,
  voiceSetApi,
  keywordApi
} from '../services/api'
import './Dashboard.css'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
  }
}

// Chart colors
const PIE_COLORS = ['#22c55e', '#ef4444', '#f59e0b', '#6b7280', '#3b82f6', '#8b5cf6']
const BOT_STATUS_COLORS = {
  active: '#22c55e',    // Green - actively on calls
  waiting: '#f59e0b',   // Amber - logged out
  paused: '#3b82f6',    // Blue - paused
  offline: '#6b7280',   // Gray - offline
  crashed: '#ef4444'    // Red - crashed
}

// Custom tooltip for charts
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="dash-tooltip">
        <p className="dash-tooltip-label">{payload[0].name}</p>
        <p className="dash-tooltip-value">{payload[0].value.toLocaleString()}</p>
      </div>
    )
  }
  return null
}

const formatCompact = (n) => {
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
  if (n >= 10000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
  return n.toLocaleString()
}

function Dashboard() {
  const { user, isAdmin } = useAuth()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Data state
  const [botSummary, setBotSummary] = useState(null)
  const [statsOverview, setStatsOverview] = useState(null)
  const [dialersSummary, setDialersSummary] = useState(null)
  const [serversStats, setServersStats] = useState([])
  const [clients, setClients] = useState([])
  const [requests, setRequests] = useState([])
  const [financials, setFinancials] = useState(null)
  const [backupStats, setBackupStats] = useState(null)
  const [errors, setErrors] = useState([])
  const [dialers, setDialers] = useState([])
  const [inactiveBots, setInactiveBots] = useState([])
  const [requestStats, setRequestStats] = useState(null)
  const [avatars, setAvatars] = useState([])
  const [voiceSets, setVoiceSets] = useState([])
  const [globalCategories, setGlobalCategories] = useState([])
  const [keywordCategories, setKeywordCategories] = useState([])
  const [keywordSuggestions, setKeywordSuggestions] = useState({ high: [], medium: [], low: [] })
  const [dialerStats, setDialerStats] = useState([])

  useEffect(() => {
    loadAllData()
  }, [])

  // Helper to get bot count from request - checks bot_count, bot_ids, or sips range
  const getBotCountFromRequest = (request) => {
    // First check bot_count
    if (request.bot_count) return request.bot_count

    // Check bot_ids array
    if (request.bot_ids) {
      try {
        const ids = typeof request.bot_ids === 'string'
          ? JSON.parse(request.bot_ids)
          : request.bot_ids
        if (Array.isArray(ids) && ids.length > 0) return ids.length
      } catch (e) {
        // ignore parse error
      }
    }

    // Check sips range (e.g., "1001-1010" = 10 bots)
    if (request.sips) {
      const rangeMatch = request.sips.match(/^(\d+)-(\d+)$/)
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1], 10)
        const end = parseInt(rangeMatch[2], 10)
        return end - start + 1
      }
    }

    return 1 // default
  }

  const loadAllData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else setLoading(true)

    const now = new Date()
    // MTD: First day of current month to today
    const mtdStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
    const today = now.toISOString().split('T')[0]

    try {
      const results = await Promise.allSettled([
        monitoringApi.getSummary(),
        monitoringApi.getStatsOverview({ from: mtdStart, to: today }),
        monitoringApi.getDialersSummary(),
        monitoringApi.getServersStats(),
        clientAdminApi.getClients(),
        financialsApi.getRequests('pending'), // Use financials API like Request Inbox page
        financialsApi.getDashboard(now.getMonth() + 1, now.getFullYear()),
        backupApi.getStats(),
        monitoringApi.getErrors({ limit: 10, resolved: false }),
        dialerApi.getAll(),
        monitoringApi.getInactiveBots(3), // Bots inactive for 3+ days
        financialsApi.getRequestStats(), // Request stats from financials API
        avatarApi.getAll(), // Avatars
        voiceSetApi.getAll(), // Voice sets
        voiceSetApi.getGlobalCategories(), // Global categories - returns { success: true, categories: [...] }
        keywordApi.getAll(), // Keyword categories with keywords array (same as KeywordCategories page)
        keywordApi.getSuggestions(7, 10), // Suggestions (same as KeywordCategories page)
        monitoringApi.getStatsByDialer({ from: mtdStart, to: today }) // Dialer stats like Stats page
      ])

      if (results[0].status === 'fulfilled') setBotSummary(results[0].value.data)
      if (results[1].status === 'fulfilled') setStatsOverview(results[1].value.data)
      if (results[2].status === 'fulfilled') setDialersSummary(results[2].value.data)
      if (results[3].status === 'fulfilled') setServersStats(results[3].value.data?.servers || [])
      if (results[4].status === 'fulfilled') setClients(results[4].value.data?.clients || [])
      if (results[5].status === 'fulfilled') setRequests(results[5].value.data?.requests || [])
      if (results[6].status === 'fulfilled') setFinancials(results[6].value.data)
      if (results[7].status === 'fulfilled') setBackupStats(results[7].value.data?.data?.summary || results[7].value.data?.summary || results[7].value.data)
      if (results[8].status === 'fulfilled') setErrors(results[8].value.data?.errors || [])
      if (results[9].status === 'fulfilled') setDialers(Array.isArray(results[9].value.data) ? results[9].value.data : [])
      if (results[10].status === 'fulfilled') setInactiveBots(results[10].value.data?.bots || [])
      if (results[11].status === 'fulfilled') setRequestStats(results[11].value.data?.stats || null)
      if (results[12].status === 'fulfilled') setAvatars(Array.isArray(results[12].value.data) ? results[12].value.data : [])
      if (results[13].status === 'fulfilled') setVoiceSets(Array.isArray(results[13].value.data) ? results[13].value.data : [])
      if (results[14].status === 'fulfilled') setGlobalCategories(results[14].value.data?.categories || [])
      if (results[15].status === 'fulfilled') setKeywordCategories(Array.isArray(results[15].value.data) ? results[15].value.data : [])
      if (results[16].status === 'fulfilled') setKeywordSuggestions(results[16].value.data?.suggestions || { high: [], medium: [], low: [] })
      if (results[17].status === 'fulfilled') setDialerStats(results[17].value.data?.dialers || [])
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  // Computed KPIs
  const kpis = useMemo(() => {
    // Bot Activity - use dialersSummary.totals for accurate counts (same as Dialers page)
    // dialersSummary.totals.active = bots with fresh heartbeat and running state
    const botsOnline = dialersSummary?.totals?.active || 0
    const totalBots = dialersSummary?.totals?.total || botSummary?.bots?.total || 0
    const botsOffline = totalBots - botsOnline

    // MTD stats from statsOverview (uses date range from mtdStart to today)
    const mtdCalls = statsOverview?.overview?.total_calls || 0
    const mtdTransfers = statsOverview?.overview?.transfers?.count || 0

    // Disposition stats from statsOverview
    const answeringMachines = statsOverview?.overview?.answering_machines?.count || 0
    const doNotCall = statsOverview?.overview?.dnc?.count || 0
    const notInterested = statsOverview?.overview?.not_interested?.count || 0
    const noAnswers = statsOverview?.overview?.no_answer?.count || 0

    // System Health - server stats: online (bool), vm_running, vm_count, cpu_usage
    const serversOnline = serversStats.filter(s => s.online).length
    const totalServers = serversStats.length
    const vmsRunning = serversStats.reduce((sum, s) => sum + (s.vm_running || 0), 0)
    const totalVms = serversStats.reduce((sum, s) => sum + (s.vm_count || 0), 0)
    const avgCpu = serversStats.length > 0
      ? (serversStats.reduce((sum, s) => sum + (s.cpu_usage || 0), 0) / serversStats.length).toFixed(0)
      : 0

    // Backup stats - API returns { summary: { lastBackup: { date, size, database } } }
    const lastBackupDate = backupStats?.lastBackup?.date
    let lastBackup = 'N/A'
    if (lastBackupDate) {
      const backupTime = new Date(lastBackupDate)
      const hoursAgo = Math.floor((Date.now() - backupTime.getTime()) / (1000 * 60 * 60))
      if (hoursAgo < 24) {
        lastBackup = `${hoursAgo}h ago`
      } else {
        lastBackup = backupTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
    }

    // Clients - API returns total_bots per client
    const activeClients = clients.filter(c => c.status === 'active').length
    const pausedClients = clients.filter(c => c.status === 'paused').length
    const clientBots = clients.reduce((sum, c) => sum + (c.total_bots || 0), 0)
    const needingAttention = clients.filter(c =>
      (parseFloat(c.balance) < 100 && c.status === 'active') ||
      c.status === 'suspended'
    ).length

    // Requests - use requestStats from financials API (same as Request Inbox page)
    const pendingRequests = requestStats?.pending || 0
    const inProgressRequests = requestStats?.in_progress || 0
    // Bot additions/removals should be TOTAL NUMBER OF BOTS, not number of requests
    // Use helper that checks bot_count, bot_ids, and sips range
    const botAdditions = requests
      .filter(r => r.type === 'bot_addition')
      .reduce((sum, r) => sum + getBotCountFromRequest(r), 0)
    const botRemovals = requests
      .filter(r => r.type === 'bot_removal')
      .reduce((sum, r) => sum + getBotCountFromRequest(r), 0)

    return {
      botActivity: { botsOnline, totalBots, botsOffline, mtdCalls, mtdTransfers },
      dispositions: { answeringMachines, doNotCall, notInterested, noAnswers },
      systemHealth: { serversOnline, totalServers, vmsRunning, totalVms, avgCpu, lastBackup },
      clients: { active: activeClients, paused: pausedClients, totalBots: clientBots, needingAttention },
      requests: { pending: pendingRequests, inProgress: inProgressRequests, additions: botAdditions, removals: botRemovals }
    }
  }, [botSummary, statsOverview, dialersSummary, serversStats, clients, requests, financials, backupStats, requestStats])

  // System Stats - Avatars, Voice Sets, Keywords, Backups (same as dedicated pages)
  const systemStats = useMemo(() => {
    // Avatars - total and archived (same as Avatars.jsx)
    const totalAvatars = avatars.filter(a => !a.archived).length
    const archivedAvatars = avatars.filter(a => a.archived).length

    // Voice Sets - total and global categories (same as VoiceSets.jsx)
    const totalVoiceSets = voiceSets.length
    const totalGlobalCategories = globalCategories.length

    // Keywords - computed from categories like KeywordCategories.jsx
    // totalKeywords = categories.reduce((sum, cat) => sum + (cat.keywords?.length || 0), 0)
    const totalKeywords = keywordCategories.reduce((sum, cat) => sum + (cat.keywords?.length || 0), 0)
    // Suggestions count from getSuggestions API (high priority count)
    const suggestedKeywords = (keywordSuggestions?.high?.length || 0) +
                              (keywordSuggestions?.medium?.length || 0) +
                              (keywordSuggestions?.low?.length || 0)

    // Backups - daily and monthly counts
    const dailyBackups = backupStats?.totalDailyBackups || 0
    const monthlyBackups = backupStats?.totalMonthlyBackups || 0

    return {
      avatars: { total: totalAvatars, archived: archivedAvatars },
      voiceSets: { total: totalVoiceSets, categories: totalGlobalCategories },
      keywords: { total: totalKeywords, suggested: suggestedKeywords },
      backups: { daily: dailyBackups, monthly: monthlyBackups }
    }
  }, [avatars, voiceSets, globalCategories, keywordCategories, keywordSuggestions, backupStats])

  // Actionable Lists - keep full arrays for counts, display limited in UI
  const actionableLists = useMemo(() => {
    // Pending bot additions/removals (from financialsApi - same as Request Inbox page)
    // Field is 'type' with values 'bot_addition' / 'bot_removal'
    const pendingAdditions = requests
      .filter(r => r.type === 'bot_addition' && r.status === 'pending')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    const pendingRemovals = requests
      .filter(r => r.type === 'bot_removal' && r.status === 'pending')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    // Servers - use vm_running and vm_count, calculate memory % from ram_used_gb/ram_total_gb
    const lowUtilizedAll = serversStats
      .filter(s => s.vm_count > 0 && s.vm_running < (s.vm_count * 0.3))

    const overCapacityAll = serversStats
      .filter(s => {
        const memPct = s.ram_total_gb > 0 ? (s.ram_used_gb / s.ram_total_gb) * 100 : 0
        return s.cpu_usage > 80 || memPct > 85
      })

    // Clients - same as Clients page (balance <= 50 for low balance)
    const lowBalanceAll = clients
      .filter(c => parseFloat(c.balance) <= 50 && c.status === 'active')
      .sort((a, b) => parseFloat(a.balance) - parseFloat(b.balance))

    const inactiveClientsAll = clients
      .filter(c => c.status === 'paused' || c.status === 'suspended')

    // Bots (from dialers summary for crashed, from inactiveBots API for 3+ days offline)
    const crashedBotsAll = dialersSummary?.issues?.crashed || []
    const offlineBotsAll = inactiveBots || []

    // Dialers performance - from monitoringApi.getStatsByDialer (same as Stats page)
    // Uses transfer_rate, total_calls, transfers fields
    const topPerformersAll = [...dialerStats]
      .filter(d => d.total_calls > 10)
      .sort((a, b) => (b.transfer_rate || 0) - (a.transfer_rate || 0))

    const underPerformersAll = [...dialerStats]
      .filter(d => d.total_calls > 10 && (d.transfer_rate || 0) < 5)
      .sort((a, b) => (a.transfer_rate || 0) - (b.transfer_rate || 0))

    // Errors - use the errors state directly
    const recentErrorsAll = errors || []

    return {
      requests: {
        added: pendingAdditions.slice(0, 3),
        addedTotal: pendingAdditions.length,
        removed: pendingRemovals.slice(0, 3),
        removedTotal: pendingRemovals.length
      },
      servers: {
        lowUtilized: lowUtilizedAll.slice(0, 3),
        lowUtilizedTotal: lowUtilizedAll.length,
        overCapacity: overCapacityAll.slice(0, 3),
        overCapacityTotal: overCapacityAll.length
      },
      clients: {
        lowBalance: lowBalanceAll.slice(0, 3),
        lowBalanceTotal: lowBalanceAll.length,
        inactive: inactiveClientsAll.slice(0, 3),
        inactiveTotal: inactiveClientsAll.length
      },
      bots: {
        crashed: crashedBotsAll.slice(0, 3),
        crashedTotal: crashedBotsAll.length,
        offline: offlineBotsAll.slice(0, 3),
        offlineTotal: offlineBotsAll.length
      },
      dialers: {
        topPerformers: topPerformersAll.slice(0, 3),
        topPerformersTotal: topPerformersAll.length,
        underPerformers: underPerformersAll.slice(0, 3),
        underPerformersTotal: underPerformersAll.length
      },
      errors: {
        recent: recentErrorsAll.slice(0, 3),
        recentTotal: recentErrorsAll.length
      }
    }
  }, [requests, serversStats, clients, dialersSummary, dialerStats, inactiveBots, errors])

  // Chart data preparation
  const chartData = useMemo(() => {
    // Disposition pie chart data
    const dispositionData = []
    if (statsOverview?.overview) {
      const ov = statsOverview.overview
      if (ov.transfers?.count) dispositionData.push({ name: 'Transfers', value: ov.transfers.count, color: '#22c55e' })
      if (ov.answering_machines?.count) dispositionData.push({ name: 'Ans. Machine', value: ov.answering_machines.count, color: '#f59e0b' })
      if (ov.not_interested?.count) dispositionData.push({ name: 'Not Interested', value: ov.not_interested.count, color: '#ef4444' })
      if (ov.dnc?.count) dispositionData.push({ name: 'DNC', value: ov.dnc.count, color: '#8b5cf6' })
      if (ov.no_answer?.count) dispositionData.push({ name: 'No Answer', value: ov.no_answer.count, color: '#6b7280' })
    }

    // Bot status donut data - use dialersSummary.totals for accurate breakdown
    const totals = dialersSummary?.totals || {}
    const activeCount = parseInt(totals.active) || 0
    const waitingCount = parseInt(totals.waiting) || 0
    const pausedCount = parseInt(totals.paused) || 0
    const crashedCount = parseInt(totals.crashed) || 0
    const totalBots = parseInt(totals.total) || 0
    const offlineCount = Math.max(0, totalBots - activeCount - waitingCount - pausedCount - crashedCount)

    const botStatusData = [
      { name: 'Active', value: activeCount, color: BOT_STATUS_COLORS.active },
      { name: 'Logged Out', value: waitingCount, color: BOT_STATUS_COLORS.waiting },
      { name: 'Paused', value: pausedCount, color: BOT_STATUS_COLORS.paused },
      { name: 'Offline', value: offlineCount, color: BOT_STATUS_COLORS.offline },
      { name: 'Crashed', value: crashedCount, color: BOT_STATUS_COLORS.crashed }
    ].filter(d => d.value > 0)

    // Dialer bar chart data (top 6)
    const dialerBarData = dialerStats.slice(0, 6).map(d => ({
      name: d.dialer_name?.substring(0, 15) || `Dialer ${d.dialer_id}`,
      calls: d.total_calls,
      transfers: d.transfers,
      rate: d.transfer_rate
    }))

    return { dispositionData, botStatusData, dialerBarData }
  }, [statsOverview, kpis, dialersSummary, dialerStats])

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toLocaleString()
  }

  return (
    <motion.div
      className="dashboard-modern"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div className="dash-header" variants={itemVariants}>
        <div className="dash-header-left">
          <h1><BarChart3 size={28} /> Dashboard</h1>
        </div>
        <div className="dash-header-right">
          <div className="dash-date-badge">
            <Clock size={14} />
            {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
          <button className="dash-refresh-btn" onClick={() => loadAllData(true)} disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
          </button>
        </div>
      </motion.div>

      {/* KPI Stat Blocks */}
      <motion.div className="stat-blocks" variants={itemVariants}>
        <Link to="/monitoring" className="stat-block blue">
          <div className="stat-icon"><Bot size={18} /></div>
          <div className="stat-content">
            <span className="stat-value">{loading ? '...' : `${kpis.botActivity.botsOnline}/${kpis.botActivity.totalBots}`}</span>
            <span className="stat-label">Bots Online</span>
          </div>
        </Link>
        <Link to="/stats" className="stat-block green">
          <div className="stat-icon"><PhoneForwarded size={18} /></div>
          <div className="stat-content">
            <span className="stat-value">{loading ? '...' : formatNumber(kpis.botActivity.mtdTransfers)}</span>
            <span className="stat-label">MTD Transfers</span>
          </div>
        </Link>
        <Link to="/stats" className="stat-block orange">
          <div className="stat-icon"><Phone size={18} /></div>
          <div className="stat-content">
            <span className="stat-value">{loading ? '...' : formatNumber(kpis.botActivity.mtdCalls)}</span>
            <span className="stat-label">MTD Calls</span>
          </div>
        </Link>
        {isAdmin && (
          <Link to="/servers" className="stat-block purple">
            <div className="stat-icon"><Server size={18} /></div>
            <div className="stat-content">
              <span className="stat-value">{loading ? '...' : `${kpis.systemHealth.serversOnline}/${kpis.systemHealth.totalServers}`}</span>
              <span className="stat-label">Servers Online</span>
            </div>
          </Link>
        )}
        {isAdmin && (
          <Link to="/clients" className="stat-block cyan">
            <div className="stat-icon"><Users size={18} /></div>
            <div className="stat-content">
              <span className="stat-value">{loading ? '...' : kpis.clients.active}</span>
              <span className="stat-label">Active Clients</span>
            </div>
          </Link>
        )}
        {isAdmin && (
          <Link to="/request-inbox" className="stat-block yellow">
            <div className="stat-icon"><Inbox size={18} /></div>
            <div className="stat-content">
              <span className="stat-value">{loading ? '...' : kpis.requests.pending}</span>
              <span className="stat-label">Pending Requests</span>
            </div>
          </Link>
        )}
      </motion.div>

      {/* Charts Row */}
      <motion.div className="dash-charts-row" variants={itemVariants}>
        {/* Disposition Pie Chart */}
        <div className="dash-chart-card">
          <h3 className="dash-chart-title">Call Dispositions</h3>
          {chartData.dispositionData.length > 0 ? (
            <div className="dash-chart-with-legend">
              <div className="dash-pie-container">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={chartData.dispositionData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="value">
                      {chartData.dispositionData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="dash-chart-legend">
                {chartData.dispositionData.map((item, i) => (
                  <div key={i} className="dash-legend-item">
                    <span className="dash-legend-color" style={{ background: item.color }}></span>
                    <span className="dash-legend-label">{item.name}</span>
                    <span className="dash-legend-value">{formatCompact(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="dash-chart-empty">No disposition data</div>
          )}
        </div>

        {/* Bot Status Donut */}
        <div className="dash-chart-card">
          <h3 className="dash-chart-title">Bot Status</h3>
          {chartData.botStatusData.length > 0 ? (
            <div className="dash-chart-with-legend">
              <div className="dash-pie-container">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={chartData.botStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="value">
                      {chartData.botStatusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="dash-donut-center">
                  <span className="dash-donut-value">{kpis.botActivity.totalBots}</span>
                  <span className="dash-donut-label">Total</span>
                </div>
              </div>
              <div className="dash-chart-legend">
                {chartData.botStatusData.map((item, i) => (
                  <div key={i} className="dash-legend-item">
                    <span className="dash-legend-color" style={{ background: item.color }}></span>
                    <span className="dash-legend-label">{item.name}</span>
                    <span className="dash-legend-value">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="dash-chart-empty">No bot data</div>
          )}
        </div>

        {/* Dialer Performance Bar */}
        <div className="dash-chart-card wide">
          <h3 className="dash-chart-title">Dialer Performance (MTD)</h3>
          {chartData.dialerBarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData.dialerBarData} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px' }} />
                <Bar dataKey="calls" fill="var(--text-muted)" name="Calls" radius={[0, 4, 4, 0]} />
                <Bar dataKey="transfers" fill="#22c55e" name="Transfers" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="dash-chart-empty">No dialer data</div>
          )}
        </div>
      </motion.div>

      {/* Stats-Style Panels Row */}
      <motion.div className="dash-panels-row" variants={itemVariants}>
        {/* Dialer Rankings Panel */}
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h3><Phone size={16} /> Dialer Rankings</h3>
            <Link to="/stats" className="dash-panel-action">View Stats <ExternalLink size={12} /></Link>
          </div>
          <div className="dash-panel-list">
            {loading ? (
              <div className="dash-panel-empty">Loading...</div>
            ) : dialerStats.length > 0 ? (
              dialerStats.slice(0, 5).map((dialer, i) => {
                const maxCalls = dialerStats[0]?.total_calls || 1
                const barWidth = (dialer.total_calls / maxCalls) * 100
                return (
                  <Link key={dialer.dialer_id} to={`/dialer-details/${dialer.dialer_id}`} className={`dash-panel-item ${i === 0 ? 'top' : ''}`}>
                    <div className="dash-panel-item-bar" style={{ width: `${barWidth}%` }}></div>
                    <div className="dash-panel-item-content">
                      <span className="dash-panel-rank">{i + 1}</span>
                      <span className="dash-panel-name">{dialer.dialer_name}</span>
                      <div className="dash-panel-metrics">
                        <span className="dash-metric">{dialer.total_calls?.toLocaleString()}<small>calls</small></span>
                        <span className={`dash-metric-rate ${dialer.transfer_rate >= 5 ? 'good' : dialer.transfer_rate >= 2 ? 'mid' : 'low'}`}>
                          {(dialer.transfer_rate || 0).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </Link>
                )
              })
            ) : (
              <div className="dash-panel-empty">No dialer data</div>
            )}
          </div>
        </div>

        {/* Client Alerts Panel */}
        {isAdmin && (
          <div className="dash-panel">
            <div className="dash-panel-header">
              <h3><Users size={16} /> Client Alerts</h3>
              <Link to="/clients" className="dash-panel-action">View All <ExternalLink size={12} /></Link>
            </div>
            <div className="dash-panel-list">
              {loading ? (
                <div className="dash-panel-empty">Loading...</div>
              ) : (actionableLists.clients.lowBalance.length > 0 || actionableLists.clients.inactive.length > 0) ? (
                <>
                  {actionableLists.clients.lowBalance.slice(0, 3).map((client, i) => (
                    <Link key={client.id} to={`/client-details/${client.id}`} className="dash-panel-item alert">
                      <div className="dash-panel-item-content">
                        <span className="dash-panel-icon red"><Wallet size={14} /></span>
                        <span className="dash-panel-name">{client.company_name || client.name}</span>
                        <span className="dash-metric-rate low">{formatCurrency(client.balance)}</span>
                      </div>
                    </Link>
                  ))}
                  {actionableLists.clients.inactive.slice(0, 2).map((client, i) => (
                    <Link key={client.id} to={`/client-details/${client.id}`} className="dash-panel-item">
                      <div className="dash-panel-item-content">
                        <span className="dash-panel-icon orange"><UserX size={14} /></span>
                        <span className="dash-panel-name">{client.company_name || client.name}</span>
                        <span className="dash-badge">{client.status}</span>
                      </div>
                    </Link>
                  ))}
                </>
              ) : (
                <div className="dash-panel-empty">All clients healthy</div>
              )}
            </div>
          </div>
        )}

        {/* Bot Issues Panel */}
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h3><Bot size={16} /> Bot Issues</h3>
            <Link to="/monitoring" className="dash-panel-action">View All <ExternalLink size={12} /></Link>
          </div>
          <div className="dash-panel-list">
            {loading ? (
              <div className="dash-panel-empty">Loading...</div>
            ) : (actionableLists.bots.crashed.length > 0 || actionableLists.bots.offline.length > 0) ? (
              <>
                {actionableLists.bots.crashed.slice(0, 3).map((bot, i) => (
                  <div key={i} className="dash-panel-item alert">
                    <div className="dash-panel-item-content">
                      <span className="dash-panel-icon red"><XCircle size={14} /></span>
                      <span className="dash-panel-name">{bot.sip_id || bot.name}</span>
                      <span className="dash-badge red">Crashed</span>
                    </div>
                  </div>
                ))}
                {actionableLists.bots.offline.slice(0, 2).map((bot, i) => (
                  <div key={i} className="dash-panel-item">
                    <div className="dash-panel-item-content">
                      <span className="dash-panel-icon orange"><WifiOff size={14} /></span>
                      <span className="dash-panel-name">{bot.sip_id || bot.name}</span>
                      <span className="dash-badge">{bot.days_inactive}d offline</span>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="dash-panel-empty success">All bots operational</div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Bottom Row: Requests + System Stats */}
      <motion.div className="dash-bottom-row" variants={itemVariants}>
        {/* Pending Requests */}
        {isAdmin && (
          <div className="dash-panel">
            <div className="dash-panel-header">
              <h3><Inbox size={16} /> Pending Requests</h3>
              <Link to="/request-inbox" className="dash-panel-action">View Inbox <ExternalLink size={12} /></Link>
            </div>
            <div className="dash-panel-list">
              {loading ? (
                <div className="dash-panel-empty">Loading...</div>
              ) : (actionableLists.requests.added.length > 0 || actionableLists.requests.removed.length > 0) ? (
                <>
                  {actionableLists.requests.added.slice(0, 2).map((req, i) => (
                    <Link key={i} to="/request-inbox" className="dash-panel-item">
                      <div className="dash-panel-item-content">
                        <span className="dash-panel-icon green"><Plus size={14} /></span>
                        <span className="dash-panel-name">{req.client_name || 'Client'}</span>
                        <span className="dash-badge green">+{getBotCountFromRequest(req)} bots</span>
                      </div>
                    </Link>
                  ))}
                  {actionableLists.requests.removed.slice(0, 2).map((req, i) => (
                    <Link key={i} to="/request-inbox" className="dash-panel-item">
                      <div className="dash-panel-item-content">
                        <span className="dash-panel-icon red"><Minus size={14} /></span>
                        <span className="dash-panel-name">{req.client_name || 'Client'}</span>
                        <span className="dash-badge red">-{getBotCountFromRequest(req)} bots</span>
                      </div>
                    </Link>
                  ))}
                </>
              ) : (
                <div className="dash-panel-empty">No pending requests</div>
              )}
            </div>
          </div>
        )}

        {/* System Stats Grid */}
        {isAdmin && (
          <div className="dash-system-stats">
            <Link to="/avatars" className="dash-mini-stat cyan">
              <UserCircle size={20} />
              <div className="dash-mini-stat-content">
                <span className="dash-mini-stat-value">{loading ? '...' : systemStats.avatars.total}</span>
                <span className="dash-mini-stat-label">Avatars</span>
              </div>
            </Link>
            <Link to="/voice-sets" className="dash-mini-stat purple">
              <Mic size={20} />
              <div className="dash-mini-stat-content">
                <span className="dash-mini-stat-value">{loading ? '...' : systemStats.voiceSets.total}</span>
                <span className="dash-mini-stat-label">Voice Sets</span>
              </div>
            </Link>
            <Link to="/keywords" className="dash-mini-stat green">
              <Tag size={20} />
              <div className="dash-mini-stat-content">
                <span className="dash-mini-stat-value">{loading ? '...' : systemStats.keywords.total}</span>
                <span className="dash-mini-stat-label">Keywords</span>
              </div>
            </Link>
            <div className="dash-mini-stat blue">
              <Cloud size={20} />
              <div className="dash-mini-stat-content">
                <span className="dash-mini-stat-value">{loading ? '...' : systemStats.backups.daily}</span>
                <span className="dash-mini-stat-label">Backups</span>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Recent Errors (only if errors exist) */}
      {isAdmin && actionableLists.errors.recentTotal > 0 && (
        <motion.div className="dash-errors-section" variants={itemVariants}>
          <div className="dash-panel-header">
            <h3><AlertCircle size={16} /> Recent Errors ({actionableLists.errors.recentTotal})</h3>
            <Link to="/monitoring?tab=errors" className="dash-panel-action">View All <ExternalLink size={12} /></Link>
          </div>
          <div className="dash-errors-table">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Bot</th>
                  <th>Error</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {actionableLists.errors.recent.map((error, i) => (
                  <tr key={i}>
                    <td>{new Date(error.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                    <td>{error.sip_id || 'Unknown'}</td>
                    <td>{error.message || error.error_type}</td>
                    <td><span className={`dash-error-status ${error.resolved ? 'resolved' : ''}`}>{error.resolved ? 'Resolved' : 'Open'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}

export default Dashboard
